import { getDatabase, ref, get, set, push, remove } from 'firebase/database';

/**
 * Multi-Space Helper Functions
 * Ermöglicht mehrere Website-Spaces pro User
 */

/**
 * Lädt alle Spaces für einen User
 */
export const getUserSpaces = async (userId) => {
  try {
    const db = getDatabase();
    const spacesRef = ref(db, `/users/${userId}/spaces`);
    const snapshot = await get(spacesRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const spacesData = snapshot.val();
    return Object.keys(spacesData)
      .filter(spaceId => spacesData[spaceId]) // Filter out undefined entries
      .map(spaceId => ({
        spaceId,
        ...spacesData[spaceId]
      }))
      .filter(space => space.spaceName && space.username); // Ensure required fields exist
    
  } catch (error) {
    console.error('Error loading user spaces:', error);
    return [];
  }
};

/**
 * Erstellt neuen Space mit erweiterten Design-Settings
 */
export const createSpace = async (userId, spaceName, username, designSettings = null) => {
  try {
    const db = getDatabase();
    
    // Prüfe Spacename-Verfügbarkeit
    const spacenameRef = ref(db, `/spacenames/${username}`);
    const spacenameSnapshot = await get(spacenameRef);
    
    if (spacenameSnapshot.exists()) {
      throw new Error('Spacename already taken');
    }
    
    // Erstelle Space-ID
    const spaceId = username; // Space-ID = Username für Einfachheit
    
    // Default Design Settings
    const defaultDesignSettings = {
      ownerName: '',
      contextInfo: '',
      globalBigFont: 'Voyage',
      globalSmallFont: 'Jost',
      colors: {
        color1: '#CDD1C4',
        color2: '#5C80BC',
        color3: '#EBF5DF',
        color4: '#30323D',
        color5: '#FFFFFF'
      },
      backgroundColor: '#FFFFFF'
    };
    
    // Erstelle Space mit Design-Settings
    const spaceData = {
      spaceName,
      username,
      customDomain: '',
      domainStatus: 'none', // none|pending|verified|error
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pageCount: 0,
      isActive: true,
      // Design Settings für alle Pages in diesem Space
      designSettings: designSettings || defaultDesignSettings
    };
    
    // Speichere Space
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    await set(spaceRef, spaceData);
    
    // Erstelle Spacename-Mapping
    const spacenameMapping = {
      userId,
      spaceId,
      spaceName,
      createdAt: new Date().toISOString()
    };
    
    await set(spacenameRef, spacenameMapping);
    
    console.log('✅ Space created successfully:', { spaceId, spaceName, username });
    
    return {
      spaceId,
      ...spaceData
    };
    
  } catch (error) {
    console.error('Error creating space:', error);
    throw error;
  }
};

/**
 * Löscht Space komplett inkl. aller Routes
 */
export const deleteSpace = async (userId, spaceId) => {
  try {
    const db = getDatabase();
    
    // Lade Space-Info
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    const spaceSnapshot = await get(spaceRef);
    
    if (!spaceSnapshot.exists()) {
      throw new Error('Space not found');
    }
    
    const spaceData = spaceSnapshot.val();
    const username = spaceData.username;
    
    console.log('🗑️ Deleting space:', { spaceId, username, customDomain: spaceData.customDomain });
    
    // Lösche alle Pages des Spaces
    const pagesRef = ref(db, `/pages/${userId}/${spaceId}`);
    await remove(pagesRef);
    console.log('✅ Deleted pages');
    
    // Lösche URL-Mappings
    const urlMappingsRef = ref(db, `/urlMappings/${username}`);
    await remove(urlMappingsRef);
    console.log('✅ Deleted URL mappings');
    
    // Lösche Username-Mapping (alte Struktur)
    const usernameRef = ref(db, `/usernames/${username}`);
    await remove(usernameRef);
    console.log('✅ Deleted username mapping');
    
    // Lösche Spacename-Mapping (neue Struktur)
    const spacenameRef = ref(db, `/spacenames/${username}`);
    await remove(spacenameRef);
    console.log('✅ Deleted spacename mapping');
    
    // Lösche Domain-Mapping (falls vorhanden)
    if (spaceData.customDomain) {
      const domainRef = ref(db, `/domainMappings/${spaceData.customDomain}`);
      await remove(domainRef);
      console.log('✅ Deleted domain mapping:', spaceData.customDomain);
    }
    
    // Lösche Space
    await remove(spaceRef);
    console.log('✅ Deleted space');
    
    console.log('✅ Space completely deleted:', spaceId);
    
  } catch (error) {
    console.error('Error deleting space:', error);
    throw error;
  }
};

/**
 * Lädt Pages für einen Space
 */
export const getSpacePages = async (userId, spaceId) => {
  try {
    const db = getDatabase();
    
    // Try 3-level structure first: /pages/{userId}/{spaceId}
    const spaceSpecificRef = ref(db, `/pages/${userId}/${spaceId}`);
    console.log('🔍 getSpacePages DEBUG: Looking for pages at 3-level path:', `/pages/${userId}/${spaceId}`);
    
    const spaceSnapshot = await get(spaceSpecificRef);
    console.log('🔍 getSpacePages DEBUG: 3-level snapshot exists:', spaceSnapshot.exists());
    
    if (spaceSnapshot.exists()) {
      const pagesData = spaceSnapshot.val();
      console.log('🔍 getSpacePages DEBUG: 3-level raw pages data:', pagesData);
      
      const result = Object.keys(pagesData).map(pageName => ({
        pageName,
        id: `${userId}/${spaceId}/${pageName}`,
        ...pagesData[pageName]
      }));
      
      // Sortiere Pages nach Erstellungsdatum (neueste zuerst)
      const sortedResult = result.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt || 0);
        const dateB = new Date(b.createdAt || b.updatedAt || 0);
        return dateB - dateA; // Neueste zuerst (descending)
      });
      
      console.log('🔍 getSpacePages DEBUG: 3-level final result (sorted newest first):', sortedResult);
      return sortedResult;
    }
    
    // Fallback: Try 2-level structure /pages/{userId} and filter by spaceId
    console.log('🔍 getSpacePages DEBUG: Trying 2-level structure fallback');
    const userPagesRef = ref(db, `/pages/${userId}`);
    const userSnapshot = await get(userPagesRef);
    
    if (!userSnapshot.exists()) {
      console.log('🔍 getSpacePages DEBUG: No pages found in 2-level structure either');
      return [];
    }
    
    const userPagesData = userSnapshot.val();
    console.log('🔍 getSpacePages DEBUG: 2-level raw user pages data:', userPagesData);
    
    const spacePages = [];
    
    // Handle both 2-level and 3-level structures (same logic as PageNav)
    Object.keys(userPagesData).forEach(key => {
      const pageOrSpace = userPagesData[key];
      console.log('🔍 getSpacePages DEBUG: Processing key:', key, 'data:', pageOrSpace);
      
      // Check if this is a space container (has nested pages, regardless of direct widgets)
      if (pageOrSpace && typeof pageOrSpace === 'object') {
        console.log('🔍 getSpacePages DEBUG: Found space container:', key, 'checking if it matches spaceId:', spaceId);
        
        // This looks like a space container - check if any nested page belongs to our spaceId
        let foundMatchingPages = false;
        Object.keys(pageOrSpace).forEach(pageName => {
          const pageData = pageOrSpace[pageName];
          console.log('🔍 getSpacePages DEBUG: Checking nested page:', pageName, 'data:', pageData);
          
          if (pageData && typeof pageData === 'object' && pageName !== 'widgets') {
            // Skip the 'widgets' array property, only process actual page objects
            console.log('🔍 getSpacePages DEBUG: Processing potential page:', pageName);
            
            // Check if this looks like a page (has page-like properties)
            const isValidPage = pageData.name || pageData.description || pageData.createdAt || pageData.globalSettings || Array.isArray(pageData.widgets);
            
            if (isValidPage) {
              // Check if this page actually belongs to our space
              const pageSpaceId = pageData.spaceId || pageData.username;
              const containerMatchesSpace = key === spaceId;
              const pageHasMatchingSpaceId = pageSpaceId === spaceId;
              
              console.log('🔍 getSpacePages DEBUG: Space matching check:', {
                containerName: key,
                requestedSpaceId: spaceId,
                pageSpaceId: pageSpaceId,
                containerMatchesSpace,
                pageHasMatchingSpaceId
              });
              
              if (containerMatchesSpace || pageHasMatchingSpaceId) {
                console.log('🔍 getSpacePages DEBUG: ✅ Page belongs to requested space, adding:', pageName);
                foundMatchingPages = true;
                spacePages.push({
                  pageName: pageName,
                  id: `${userId}/${key}/${pageName}`,
                  spaceId: spaceId,
                  slug: pageData.slug || pageName,
                  widgets: pageData.widgets || [],
                  ...pageData
                });
              } else {
                console.log('🔍 getSpacePages DEBUG: ❌ Page belongs to different space, skipping:', pageName);
              }
            } else {
              console.log('🔍 getSpacePages DEBUG: ❌ Not a valid page object:', pageName);
            }
          }
        });
        
        if (!foundMatchingPages) {
          console.log('🔍 getSpacePages DEBUG: ❌ No matching pages found in container:', key);
        }
      }
      // Also check for 2-level direct pages with spaceId property
      else if (pageOrSpace.widgets && pageOrSpace.spaceId === spaceId) {
        console.log('🔍 getSpacePages DEBUG: ✅ Found 2-level page with matching spaceId:', key);
        spacePages.push({
          pageName: key,
          id: `${userId}/${key}`,
          slug: pageOrSpace.slug || key,
          ...pageOrSpace
        });
      }
      else {
        console.log('🔍 getSpacePages DEBUG: ❌ Key does not match spaceId and no matching spaceId property found');
      }
    });
    
    console.log('🔍 getSpacePages DEBUG: 2-level fallback result:', spacePages);
    
    // Sortiere Pages nach Erstellungsdatum (neueste zuerst)
    const sortedPages = spacePages.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt || 0);
        const dateB = new Date(b.createdAt || b.updatedAt || 0);
        return dateB - dateA; // Neueste zuerst (descending)
    });
    
    console.log('📄 getSpacePages: Sorted pages by creation date (newest first)');
    return sortedPages;
    
  } catch (error) {
    console.error('Error loading space pages:', error);
    return [];
  }
};

/**
 * Aktualisiert Space-Informationen
 */
export const updateSpace = async (userId, spaceId, updates) => {
  try {
    const db = getDatabase();
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    
    const updatedData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await set(spaceRef, updatedData);
    
    console.log('✅ Space updated successfully:', spaceId);
    return updatedData;
    
  } catch (error) {
    console.error('Error updating space:', error);
    throw error;
  }
};

/**
 * Aktualisiert Page-Count für einen Space
 */
export const updateSpacePageCount = async (userId, spaceId) => {
  try {
    const db = getDatabase();
    
    // Lade aktuelle Pages
    const pages = await getSpacePages(userId, spaceId);
    const pageCount = pages.length;
    
    // Update Space mit korrekter pageCount
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    const spaceSnapshot = await get(spaceRef);
    
    if (spaceSnapshot.exists()) {
      const currentData = spaceSnapshot.val();
      await set(spaceRef, {
        ...currentData,
        pageCount,
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ Page count updated:', { spaceId, pageCount });
    }
    
    return pageCount;
  } catch (error) {
    console.error('Error updating page count:', error);
    return 0;
  }
};

/**
 * Verbindet Custom Domain mit Space
 */
export const connectDomainToSpace = async (userId, spaceId, domain) => {
  try {
    const db = getDatabase();
    
    // Update Space mit Domain
    await updateSpace(userId, spaceId, {
      customDomain: domain,
      domainStatus: 'pending'
    });
    
    // Erstelle Domain-Mapping
    const domainMapping = {
      userId,
      spaceId,
      pages: {}, // Wird später gefüllt
      settings: {
        siteName: domain,
        favicon: '',
        logoUrl: '',
        brandColor: '#ffffff'
      },
      domainStatus: 'pending',
      createdAt: new Date().toISOString()
    };
    
    const domainRef = ref(db, `/domainMappings/${domain}`);
    await set(domainRef, domainMapping);
    
    console.log('✅ Domain connected to space:', { domain, spaceId });
    
  } catch (error) {
    console.error('Error connecting domain to space:', error);
    throw error;
  }
};

/**
 * Lädt Design Settings für einen Space
 */
export const getSpaceDesignSettings = async (userId, spaceId) => {
  try {
    const db = getDatabase();
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    const snapshot = await get(spaceRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const spaceData = snapshot.val();
    return spaceData.designSettings || null;
    
  } catch (error) {
    console.error('Error loading space design settings:', error);
    return null;
  }
};

/**
 * Aktualisiert Design Settings für einen Space
 */
export const updateSpaceDesignSettings = async (userId, spaceId, designSettings) => {
  try {
    const db = getDatabase();
    const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
    
    // Lade aktuelle Space-Daten
    const snapshot = await get(spaceRef);
    if (!snapshot.exists()) {
      throw new Error('Space not found');
    }
    
    const currentData = snapshot.val();
    const updatedData = {
      ...currentData,
      designSettings: {
        ...currentData.designSettings,
        ...designSettings
      },
      updatedAt: new Date().toISOString()
    };
    
    await set(spaceRef, updatedData);
    
    console.log('✅ Space design settings updated:', spaceId);
    return updatedData.designSettings;
    
  } catch (error) {
    console.error('Error updating space design settings:', error);
    throw error;
  }
};

/**
 * Validiert Space-Namen
 */
export const validateSpaceName = (spaceName) => {
  if (!spaceName || spaceName.trim().length < 2) {
    return { valid: false, error: 'Space name must be at least 2 characters' };
  }
  
  if (spaceName.length > 50) {
    return { valid: false, error: 'Space name must be less than 50 characters' };
  }
  
  return { valid: true };
};

/**
 * Erstellt neue Page mit Space Design Settings
 */
export const createPageWithSpaceDesign = async (userId, spaceId, pageData) => {
  try {
    const db = getDatabase();
    
    // Lade Space Design Settings
    const spaceDesignSettings = await getSpaceDesignSettings(userId, spaceId);
    
    if (!spaceDesignSettings) {
      console.warn('No space design settings found, using defaults');
    }
    
    // Merge Page Data mit Space Design Settings
    const pageWithDesign = {
      ...pageData,
      globalSettings: {
        font: spaceDesignSettings?.globalBigFont || 'Voyage',
        smallFont: spaceDesignSettings?.globalSmallFont || 'Jost',
        colorScheme: "SpaceInherited"
      },
      colors: spaceDesignSettings?.colors || {
        color1: '#CDD1C4',
        color2: '#5C80BC',
        color3: '#EBF5DF',
        color4: '#30323D',
        color5: '#FFFFFF'
      },
      backgroundColor: spaceDesignSettings?.backgroundColor || '#FFFFFF',
      designInheritance: {
        inheritsFromSpace: true,
        spaceId: spaceId,
        lastInheritedAt: new Date().toISOString()
      },
      updatedAt: new Date().toISOString()
    };
    
    console.log('✅ Page created with space design inheritance:', pageWithDesign);
    return pageWithDesign;
    
  } catch (error) {
    console.error('Error creating page with space design:', error);
    throw error;
  }
};

/**
 * Generiert Username aus Space-Namen
 */
export const generateUsernameFromSpaceName = (spaceName) => {
  return spaceName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Nur Buchstaben, Zahlen, Leerzeichen, Bindestriche
    .replace(/\s+/g, '-')         // Leerzeichen → Bindestriche
    .replace(/-+/g, '-')          // Mehrfache Bindestriche → Einzelne
    .replace(/^-+|-+$/g, '')      // Bindestriche am Anfang/Ende entfernen
    .substring(0, 20);            // Max 20 Zeichen
};

/**
 * Dupliziert einen Space (ohne Domain-Settings)
 */
export const duplicateSpace = async (userId, originalSpaceId, newSpaceName, newUsername) => {
  try {
    const db = getDatabase();
    
    // Prüfe ob Username verfügbar ist
    const spacenameRef = ref(db, `/spacenames/${newUsername}`);
    const spacenameSnapshot = await get(spacenameRef);
    
    if (spacenameSnapshot.exists()) {
      throw new Error('Username already taken');
    }
    
    // Lade Original Space
    const originalSpaceRef = ref(db, `/users/${userId}/spaces/${originalSpaceId}`);
    const originalSpaceSnapshot = await get(originalSpaceRef);
    
    if (!originalSpaceSnapshot.exists()) {
      throw new Error('Original space not found');
    }
    
    const originalSpaceData = originalSpaceSnapshot.val();
    
    // Erstelle neuen Space mit kopierten Einstellungen (OHNE Domain Settings)
    const newSpaceId = newUsername; // Space-ID = Username
    const newSpaceData = {
      spaceName: newSpaceName,
      username: newUsername,
      // ❌ Domain Settings werden NICHT übernommen
      customDomain: '',
      domainStatus: 'none',
      // ✅ Design Settings werden übernommen
      designSettings: originalSpaceData.designSettings || {
        ownerName: '',
        contextInfo: '',
        globalBigFont: 'Voyage',
        globalSmallFont: 'Jost',
        colors: {
          color1: '#CDD1C4',
          color2: '#5C80BC',
          color3: '#EBF5DF',
          color4: '#30323D',
          color5: '#FFFFFF'
        },
        backgroundColor: '#FFFFFF'
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pageCount: 0,
      isActive: true,
      duplicatedFrom: originalSpaceId // Tracking
    };
    
    // Speichere neuen Space
    const newSpaceRef = ref(db, `/users/${userId}/spaces/${newSpaceId}`);
    await set(newSpaceRef, newSpaceData);
    
    // Erstelle Spacename-Mapping
    const newSpacenameMapping = {
      userId,
      spaceId: newSpaceId,
      spaceName: newSpaceName,
      createdAt: new Date().toISOString()
    };
    
    await set(spacenameRef, newSpacenameMapping);
    
    // Kopiere alle Pages vom Original Space
    const originalPages = await getSpacePages(userId, originalSpaceId);
    
    for (const originalPage of originalPages) {
      const newPageKey = originalPage.pageName;
      
      // Erstelle kopierte Page
      const newPageData = {
        ...originalPage,
        userId,
        spaceId: newSpaceId,
        username: newUsername,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        duplicatedFrom: `${originalSpaceId}/${originalPage.pageName}`
      };
      
      // Lösche alte IDs/Refs
      delete newPageData.id;
      delete newPageData.pageName;
      
      // Speichere kopierte Page
      const newPageRef = ref(db, `/pages/${userId}/${newSpaceId}/${newPageKey}`);
      await set(newPageRef, newPageData);
      
      // Erstelle URL-Mapping
      const urlPath = originalPage.isHomePage ? '' : originalPage.slug || newPageKey;
      const urlMappingRef = ref(db, `/urlMappings/${newUsername}/${urlPath}`);
      await set(urlMappingRef, {
        pageId: `${userId}/${newSpaceId}/${newPageKey}`,
        userId,
        spaceId: newSpaceId,
        username: newUsername,
        isHomePage: originalPage.isHomePage || false
      });
    }
    
    // Update Page Count
    await updateSpacePageCount(userId, newSpaceId);
    
    console.log('✅ Space duplicated successfully:', {
      originalSpaceId,
      newSpaceId,
      pagesCopied: originalPages.length
    });
    
    return {
      spaceId: newSpaceId,
      ...newSpaceData,
      pageCount: originalPages.length
    };
    
  } catch (error) {
    console.error('Error duplicating space:', error);
    throw error;
  }
};
